import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Zap, Star, RefreshCw, ChevronDown, ChevronUp, Target, Shield } from 'lucide-react';
import { opportunitiesAPI } from '../services/api';

const TIER_CONFIG = {
  S: { label: "S", color: "text-amber-300", bg: "bg-amber-500/20 border-amber-500/40", glow: "shadow-amber-500/20", desc: "Exceptionnelle" },
  A: { label: "A", color: "text-emerald-400", bg: "bg-emerald-500/15 border-emerald-500/30", glow: "shadow-emerald-500/10", desc: "Forte" },
  B: { label: "B", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/25", glow: "", desc: "Correcte" },
};

function OpportunityCard({ opp, onAnalyze }) {
  const tier = TIER_CONFIG[opp.tier] || TIER_CONFIG.B;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      data-testid={`opp-card-${opp.ticker}`}
      className={`border rounded-lg p-3 transition-all hover:scale-[1.01] cursor-pointer ${tier.bg} ${tier.glow ? 'shadow-lg ' + tier.glow : ''}`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-sm ${tier.bg} ${tier.color}`}>
            {tier.label}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-white text-sm">{opp.ticker}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${opp.region === 'EU' ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-500/20 text-slate-400'}`}>
                {opp.region}
              </span>
            </div>
            <span className="text-[11px] text-slate-400 block truncate max-w-[150px]">{opp.name}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-white">${opp.price?.toFixed(2)}</div>
          <div className={`text-xs font-medium ${opp.daily_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {opp.daily_change >= 0 ? '+' : ''}{opp.daily_change?.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <div className="flex items-center gap-1 text-[10px] text-slate-400">
          <Target className="w-3 h-3" />
          RSI {opp.rsi?.toFixed(0)}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-slate-400">
          <TrendingUp className="w-3 h-3" />
          Vol {opp.vol_ratio?.toFixed(1)}x
        </div>
        <div className={`text-[10px] font-medium ml-auto ${tier.color}`}>
          Score {opp.score}
        </div>
        {expanded ? <ChevronUp className="w-3 h-3 text-slate-500" /> : <ChevronDown className="w-3 h-3 text-slate-500" />}
      </div>

      {expanded && (
        <div className="mt-2 pt-2 border-t border-slate-700/50 space-y-1">
          {opp.reasons?.map((r, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-300">
              <Zap className={`w-3 h-3 mt-0.5 shrink-0 ${tier.color}`} />
              <span>{r}</span>
            </div>
          ))}
          <button
            data-testid={`opp-analyze-${opp.ticker}`}
            onClick={(e) => { e.stopPropagation(); onAnalyze?.(opp.ticker); }}
            className="mt-2 w-full text-center text-[11px] py-1.5 rounded bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 transition-colors"
          >
            Analyser {opp.ticker}
          </button>
        </div>
      )}
    </div>
  );
}

export default function OpportunitiesPanel({ onAnalyzeTicker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filter, setFilter] = useState("all"); // all | S | A | B | EU | US

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    try {
      const d = await opportunitiesAPI.getRanked();
      setData(d);
      setLastUpdate(new Date());
    } catch (e) {
      console.error("Opportunities fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOpportunities();
    const interval = setInterval(fetchOpportunities, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchOpportunities]);

  const opportunities = data?.opportunities || [];
  const counts = data?.counts || { S: 0, A: 0, B: 0 };

  const filtered = opportunities.filter(o => {
    if (filter === "all") return true;
    if (filter === "S" || filter === "A" || filter === "B") return o.tier === filter;
    if (filter === "EU") return o.region === "EU";
    if (filter === "US") return o.region === "US";
    return true;
  });

  return (
    <div data-testid="opportunities-panel" className="rounded-xl border border-slate-700/50 bg-slate-900/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-amber-500/15">
            <Star className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">Opportunites d'Achat <span className="text-amber-400/80 text-[10px] font-bold ml-1">— Entrées à bon prix</span></h3>
            <p className="text-[10px] text-slate-500">
              <span className="text-slate-400">Pullback / oversold</span> · supports techniques + volume
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {counts.S > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 animate-pulse">
              {counts.S} S-TIER
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/15 text-emerald-400">
            {counts.A} A
          </span>
          <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-500/10 text-blue-400">
            {counts.B} B
          </span>
          <button
            data-testid="opp-refresh-btn"
            onClick={fetchOpportunities}
            disabled={loading}
            className="p-1.5 rounded-md hover:bg-slate-700/50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-slate-700/30 flex items-center gap-1.5 overflow-x-auto">
        {[
          { key: "all", label: "Tous" },
          { key: "S", label: "S-Tier" },
          { key: "A", label: "A-Tier" },
          { key: "B", label: "B-Tier" },
          { key: "EU", label: "EU" },
          { key: "US", label: "US" },
        ].map(f => (
          <button
            key={f.key}
            data-testid={`opp-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
              filter === f.key
                ? 'bg-slate-600/50 text-white'
                : 'text-slate-500 hover:text-slate-300 hover:bg-slate-700/30'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
        {loading && !data ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
            Scan en cours...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-6 text-slate-500 text-sm">
            <Shield className="w-5 h-5 mx-auto mb-2 opacity-50" />
            Aucune opportunite {filter !== "all" ? `(filtre: ${filter})` : "detectee"}
          </div>
        ) : (
          filtered.map((opp, i) => (
            <OpportunityCard key={opp.ticker} opp={opp} onAnalyze={onAnalyzeTicker} />
          ))
        )}
      </div>

      {lastUpdate && (
        <div className="px-4 py-1.5 border-t border-slate-700/30 text-[10px] text-slate-600 text-right">
          Mis a jour: {lastUpdate.toLocaleTimeString("fr-FR")}
        </div>
      )}
    </div>
  );
}
